/**
 *
 * CoursesMobile
 *
 */

import React, { memo, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';

import moment, { localeFr } from 'utils/moment/momentLocale';

// styles
import './style.scss';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import reducer from './reducer';
import saga from './saga';

import { flushState, loadMobileCourses, updateCourse } from './actions';
import {
  makeSelectLoading,
  makeSelectError,
  makeSelectItemsList,
  makeSelectCourse,
  makeSelectAppointments
} from './selectors';

// antd component
import { Layout, Switch } from 'antd';
const { Content } = Layout;

// components
import CoursesList from 'mobile/components/CoursesList';
import TCFCalendar from 'components/TCFCalendar';

// global user
import { useGetLocalUser, useGetLocalActiveChild, useGetUserChildren } from 'engine/Contexts/User.context';

// utils
import { DAY_OF_WEEK } from 'utils/helpers/helper.constant';
import { getObjId, getArrayIds } from 'utils/helpers/helper.general';
import { useRefState } from 'utils/hooks';

export function CoursesMobile({
  // main data
  courseDetail,
  itemsList,
  appointments,
  firstElem,
  // states
  history,
  error,
  loading,
  // actions
  loadMobileCourses,
  updateCourse,
  flushState,
}) {
  useInjectReducer({ key: 'coursesMobile', reducer });
  useInjectSaga({ key: 'coursesMobile', saga });

  const now = moment();
  const week = now.isoWeekday();
  const localUser = useGetLocalUser()();
  const localActiveChild = useGetLocalActiveChild()();
  const children = useGetUserChildren()();

  let initOptions = {
    course_week: DAY_OF_WEEK[week - 1]?.value,
    onDate: now
  };

  if (localUser?.role == 'parent') {
    initOptions.students = getArrayIds(children);
  } else if (localUser?.role == 'teacher') {
    initOptions._id = localUser?._id;
  }

  const [child, setChild] = useState(localActiveChild || null);
  const [selectedDate, setSelectedDate] = useState(now);

  const [optionsData, setOptionsData] = useState(initOptions);
  const [showAll, showAllRef, setShowAll] = useRefState(false);

  const [currentCourses, setCurrentCourses] = useState([]);
  const [currentAppointments, setCurrentAppointments] = useState([]);

  // Clearing the state after unmounting a component
  // - work like ComponentDidUpdate
  useEffect(() => {
    (!itemsList || !appointments) && loadMobileCourses(optionsData);
    return () => {
      // Anything in here is fired on component unmount.
      flushState();
    };
  }, []);

  // Loading data when optionsData is changed
  // - work like ComponentDidUpdate
  useEffect(() => {
    loadMobileCourses(optionsData);
  }, [optionsData]);

  useEffect(() => {
    if (!!itemsList && itemsList.length > 0 || !!appointments && appointments?.length > 0) {
      if (localUser?.role == 'parent' && child && !showAll) {
        const listStd = getActiveChildCourses(itemsList);
        const pointStd = getActiveChildAppointments(appointments);
        setCurrentCourses(listStd);
        setCurrentAppointments(pointStd);
      } else {
        setCurrentCourses(itemsList);
        setCurrentAppointments(appointments);
      }
    } else {
      setCurrentCourses([]);
      setCurrentAppointments([]);
    }
  }, [itemsList, child, showAll]);

  useEffect(() => {
    if (getObjId(localActiveChild) != getObjId(child)) {
      setChild(localActiveChild);
    }
  }, [localActiveChild?._id]);

  const getActiveChildCourses = useCallback(list => {
    if (localUser?.role == 'parent' && child && list) {
      return list.filter(course =>
        course?.students.find(el => getObjId(el) == getObjId(child)),
      );
    } else {
      return [];
    }
  }, [child, localUser]);
  
  const getActiveChildAppointments = useCallback(list => {
    if (localUser?.role == 'parent' && child && list) {
      return list.filter(point =>
        getObjId(point?.adminAnswer?.student) == getObjId(child)
      );
    } else {
      return [];
    }
  }, [child, localUser]);

  const onCalendarSelect = useCallback(value => {
    const date = moment(value);
    setSelectedDate(date);
    // 1 - Monday, 7 - Sunday
    const week2 = value.isoWeekday();
    const datas = {
      ...optionsData,
      course_week: DAY_OF_WEEK[week2 - 1]?.value,
      onDate: date.toISOString()
    };
    setOptionsData(datas);
  },[optionsData]);

  const showAllHandle = useCallback(() => {
    setShowAll(prev => {
      if (!prev === true) {
        setCurrentCourses(itemsList);
      } else if ( !prev === false ) {
        const stdC = getActiveChildCourses(itemsList);
        setCurrentCourses(stdC);
      }
      return !prev;
    });
  }, [itemsList]);

  // render function
  return (
    <Layout className="main-full-content">
      <Helmet>
        <title>Cours</title>
        <meta name="description" content="Description of Cours" />
      </Helmet>
      <Content className="main-content-container courses-page">
        <TCFCalendar
          valueState={selectedDate}
          onSelect={onCalendarSelect}
        />

        <CoursesList
          selectedDate={moment(selectedDate).toISOString()}
          dataSourse={currentCourses}
          appointments={currentAppointments}
          className="courses-list-wrapper"
          showAll={showAll}
          loading={loading}
        />

        {localUser?.role == 'parent' && (
          <div className="courses-list-wrapper__switch">
            <Switch
              className="switch-btn"
              checked={showAll}
              onChange={showAllHandle}
            />
            <p className="switch-title">
              Afficher le plan de cours de tous mes enfants
            </p>
          </div>
        )}
      </Content>
    </Layout>
  );
}

CoursesMobile.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  loading: makeSelectLoading(),
  error: makeSelectError(),
  itemsList: makeSelectItemsList(),
  appointments: makeSelectAppointments(),
  courseDetail: makeSelectCourse(),
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    flushState: () => dispatch(flushState()),
    loadMobileCourses: options => dispatch(loadMobileCourses(options)),
    updateCourse: itemData => dispatch(updateCourse(itemData)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo
)(CoursesMobile);
